import clsx from 'clsx';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Play,
  Scissors,
  Square,
  Upload,
  Wand2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { audioCtx } from '../audio/context';
import { registerBuffer } from '../audio/decode';
import { engine } from '../audio/engine';
import { extractLoop, findLoopCandidates, type LoopCandidate } from '../audio/loops';
import { analyseTrack, type Progress, type TrackAnalysis } from '../audio/trackAnalysis';
import { useStore } from '../store/useStore';
import { TrackOverview } from './TrackOverview';
import { NumberField, Segmented } from './ui/Controls';

const BAR_OPTIONS = [1, 2, 4, 8, 16];

function timecode(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, '0')}`;
}

/**
 * Full-track import.
 *
 * Load a whole song, let the analyser lock a bar grid across the entire thing,
 * then pull four- or eight-bar loops out of it. The suggestion list ranks
 * bar-aligned windows by how strongly they repeat, which is a decent proxy for
 * "this is a loop and not a fill".
 */
export function LoopFinder({ onClose }: { onClose: () => void }) {
  const project = useStore((s) => s.project);
  const store = useStore();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [buffer, setBuffer] = useState<AudioBuffer | null>(null);
  const [analysis, setAnalysis] = useState<TrackAnalysis | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [bpm, setBpm] = useState(120);
  const [offset, setOffset] = useState(0);
  const [bars, setBars] = useState(4);
  /** Loop start in bars from the grid origin. Fractional when snapping finer. */
  const [startBar, setStartBar] = useState(0);
  /** Snap resolution in bars. */
  const [snapBars, setSnapBars] = useState(1);
  const [previewing, setPreviewing] = useState(false);
  const [phase, setPhase] = useState<number | null>(null);

  const bpb = project.beatsPerBar;
  const barSec = (60 / bpm) * bpb;
  const duration = buffer?.duration ?? 0;
  const totalBars = Math.max(1, Math.floor((duration - offset) / barSec));

  const selection = useMemo(() => {
    const start = offset + startBar * barSec;
    return { start, end: Math.min(duration, start + bars * barSec) };
  }, [offset, startBar, barSec, bars, duration]);

  const candidates = useMemo<LoopCandidate[]>(() => {
    if (!analysis || !buffer) return [];
    return findLoopCandidates(analysis.envelope, {
      bpm,
      offsetSec: offset,
      beatsPerBar: bpb,
      bars,
      duration: buffer.duration,
      limit: 8,
    });
  }, [analysis, buffer, bpm, offset, bpb, bars]);

  const stopPreview = useCallback(() => {
    engine.stopPreviewLoop();
    setPreviewing(false);
    setPhase(null);
  }, []);

  // Tear the preview down when the screen closes, not just when it's toggled.
  useEffect(() => stopPreview, [stopPreview]);


  useEffect(() => {
    if (!previewing) return;
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      setPhase(engine.previewPhase());
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [previewing]);

  const load = useCallback(
    async (file: File) => {
      stopPreview();
      setError(null);
      setAnalysis(null);
      setBuffer(null);
      setName(file.name.replace(/\.[^.]+$/, ''));
      setProgress({ stage: 'Decoding', fraction: 0 });
      try {
        const decoded = await audioCtx().decodeAudioData(await file.arrayBuffer());
        setBuffer(decoded);
        const result = await analyseTrack(decoded, bpb, setProgress);
        setAnalysis(result);
        setBpm(Math.round(result.bpm * 100) / 100);
        setOffset(result.offset);
        setStartBar(0);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setProgress(null);
      }
    },
    [bpb, stopPreview],
  );

  // Land on the strongest suggestion as soon as there is one to land on.
  const autoSelected = useRef(false);
  useEffect(() => {
    if (!candidates.length || autoSelected.current) return;
    autoSelected.current = true;
    const best = [...candidates].sort((a, b) => b.score - a.score)[0];
    setStartBar(best.bar - 1);
  }, [candidates]);

  /** Snaps a bar position to the chosen resolution and keeps the loop in range. */
  const snapBar = useCallback(
    (b: number) => {
      const snapped = Math.round(b / snapBars) * snapBars;
      const max = Math.max(0, totalBars - bars);
      // Re-round after clamping so the end of the file can't leave it off-grid.
      return Math.max(0, Math.min(max, snapped));
    },
    [totalBars, bars, snapBars],
  );

  const currentLoop = useCallback(() => {
    if (!buffer) return null;
    return extractLoop(audioCtx(), buffer, selection.start, selection.end);
  }, [buffer, selection]);

  const togglePreview = useCallback(async () => {
    if (previewing) {
      stopPreview();
      return;
    }
    const loop = currentLoop();
    if (!loop) return;
    await engine.startPreviewLoop(loop);
    setPreviewing(true);
  }, [previewing, stopPreview, currentLoop]);

  // Restart the preview when the selection moves, so it always plays what you
  // are looking at.
  useEffect(() => {
    if (!previewing) return;
    const loop = currentLoop();
    if (loop) void engine.startPreviewLoop(loop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.start, selection.end, bpm]);

  const importRegion = useCallback(
    (startSec: number, endSec: number, label: string) => {
      if (!buffer) return;
      const loop = extractLoop(audioCtx(), buffer, startSec, endSec);
      const { meta } = registerBuffer(loop, label, bpb, { bpm });
      store.importSource(meta, true);
    },
    [buffer, bpb, bpm, store],
  );

  /** Importing is the end of this screen's job — hand over to the sequencer. */
  const finish = useCallback(() => {
    stopPreview();
    store.setUi({ view: 'sequence' });
    onClose();
  }, [stopPreview, store, onClose]);

  const importSelection = useCallback(() => {
    const at = Math.round((startBar + 1) * 100) / 100;
    importRegion(selection.start, selection.end, `${name} bar ${at}`);
    store.notify(`Imported ${bars}-bar loop from bar ${at}`);
    finish();
  }, [importRegion, selection, name, startBar, bars, store, finish]);

  const importAll = useCallback(() => {
    for (const c of candidates) {
      importRegion(c.startSec, c.endSec, `${name} bar ${c.bar}`);
    }
    store.notify(`Imported ${candidates.length} loops`);
    finish();
  }, [candidates, importRegion, name, store, finish]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(t.tagName)) return;
      if (e.key === 'Escape') onClose();
      if (e.code === 'Space') {
        e.preventDefault();
        void togglePreview();
      }
      if (e.key === 'ArrowLeft') setStartBar((b) => snapBar(b - (e.shiftKey ? bars : snapBars)));
      if (e.key === 'ArrowRight') setStartBar((b) => snapBar(b + (e.shiftKey ? bars : snapBars)));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, togglePreview, snapBar, bars, snapBars]);

  // Changing the snap or the length can leave the start off the new grid.
  useEffect(() => {
    setStartBar((b) => snapBar(b));
  }, [snapBars, bars, snapBar]);

  /** "13" on a bar line, "13.3" when it sits on a beat inside one. */
  const barLabel = (b: number) => {
    const bar = Math.floor(b + 1e-6) + 1;
    const beat = Math.round(((b % 1) + 1) % 1 * bpb);
    return beat === 0 ? `${bar}` : `${bar}.${beat + 1}`;
  };

  const selected = candidates.find((c) => c.bar === startBar + 1);

  return (
    <div
      data-modal-open
      className="fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        data-tour="finder"
        className="panel zoom-in flex max-h-full w-[min(96rem,96vw)] flex-col gap-3 overflow-y-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <Scissors size={15} className="text-volt-500" />
          <h2 className="display text-sm">Find loops in a track</h2>
          <span className="mono text-[11px] text-ink-400">
            {buffer ? `${name} · ${timecode(duration)}` : 'Load a full song and pull loops out of it'}
          </span>
          <button className="btn btn-icon ml-auto" onClick={onClose}>
            <X size={13} />
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <button className="btn btn-primary" onClick={() => fileRef.current?.click()}>
            <Upload size={13} />
            {buffer ? 'Load another track' : 'Load a track'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.wav,.mp3,.flac,.ogg,.m4a"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void load(f);
            }}
          />

          {buffer && (
            <>
              <label className="flex flex-col gap-0.5">
                <span className="label leading-none">Track BPM</span>
                <div className="flex items-center gap-1">
                  <NumberField
                    value={bpm}
                    min={20}
                    max={400}
                    step={0.01}
                    width="w-24"
                    onChange={(v) => setBpm(Math.max(20, Math.min(400, v)))}
                  />
                  <button className="btn btn-icon" onClick={() => setBpm((b) => b / 2)}>
                    ÷2
                  </button>
                  <button className="btn btn-icon" onClick={() => setBpm((b) => b * 2)}>
                    ×2
                  </button>
                </div>
              </label>

              <label className="flex flex-col gap-0.5">
                <span className="label leading-none">Grid offset</span>
                <div className="flex items-center gap-1">
                  <button
                    className="btn btn-icon"
                    onClick={() => setOffset((o) => Math.max(0, o - 0.005))}
                  >
                    <ChevronLeft size={12} />
                  </button>
                  <NumberField
                    value={Math.round(offset * 10000) / 10}
                    step={1}
                    width="w-24"
                    suffix="ms"
                    onChange={(v) => setOffset(Math.max(0, v / 1000))}
                  />
                  <button className="btn btn-icon" onClick={() => setOffset((o) => o + 0.005)}>
                    <ChevronRight size={12} />
                  </button>
                </div>
              </label>

              <label className="flex flex-col gap-0.5">
                <span className="label leading-none">Loop length</span>
                <Segmented<string>
                  value={String(bars)}
                  onChange={(v) => {
                    setBars(Number(v));
                    autoSelected.current = false;
                  }}
                  options={BAR_OPTIONS.map((b) => ({
                    value: String(b),
                    label: `${b} bar${b > 1 ? 's' : ''}`,
                  }))}
                />
              </label>

              <label className="flex flex-col gap-0.5">
                <span className="label leading-none">Drag snaps to</span>
                <Segmented<string>
                  value={String(snapBars)}
                  onChange={(v) => setSnapBars(Number(v))}
                  options={[
                    { value: '1', label: 'Bar', title: 'Move the loop start a bar at a time' },
                    { value: '0.5', label: '½ bar', title: 'Half a bar' },
                    {
                      value: String(1 / bpb),
                      label: 'Beat',
                      title: `A single beat (1/${bpb} bar)`,
                    },
                  ]}
                />
              </label>
            </>
          )}
        </div>

        {progress && (
          <div className="space-y-1.5 py-6">
            <div className="flex items-center gap-2 text-[11px] text-ink-300">
              <Loader2 size={13} className="animate-spin text-volt-500" />
              {progress.stage}…
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-ink-700">
              <div
                className="shimmer h-full bg-volt-500 transition-[width] duration-150"
                style={{ width: `${Math.round(progress.fraction * 100)}%` }}
              />
            </div>
          </div>
        )}

        {error && (
          <p className="rounded-lg border border-rose-hot/40 bg-rose-hot/10 px-3 py-2 text-[11px] text-rose-hot">
            {error}
          </p>
        )}

        {!buffer && !progress && !error && (
          <div className="py-12 text-center">
            <p className="mx-auto max-w-md text-xs leading-relaxed text-ink-400">
              Drop in a full track and Happy Chopper locks a bar grid across the whole thing, then ranks
              every bar-aligned window by how strongly it repeats. Pick the 4 or 8 bars you want and
              send them straight to the loop library.
            </p>
          </div>
        )}

        {buffer && analysis && (
          <>
            <TrackOverview
              peaks={analysis.peaks}
              duration={duration}
              bpm={bpm}
              offset={offset}
              beatsPerBar={bpb}
              selection={selection}
              candidates={candidates}
              snapBars={snapBars}
              playhead={phase}
              onScrub={(t) => setStartBar(snapBar((t - offset) / barSec))}
            />

            <div className="flex flex-wrap items-center gap-2">
              <button className="btn btn-icon" onClick={() => setStartBar((b) => snapBar(b - snapBars))}>
                <ChevronLeft size={13} />
              </button>
              <span className="mono min-w-40 text-center text-[11px]">
                bar {barLabel(startBar)} – {barLabel(startBar + bars)} of {totalBars}
              </span>
              <button className="btn btn-icon" onClick={() => setStartBar((b) => snapBar(b + snapBars))}>
                <ChevronRight size={13} />
              </button>
              <span className="mono text-[11px] text-ink-400">
                {timecode(selection.start)} → {timecode(selection.end)} ·{' '}
                {(selection.end - selection.start).toFixed(2)}s
              </span>
              {selected && (
                <span className="mono text-[11px] text-amber-hot">
                  repeats {Math.round(selected.repetition * 100)}%
                </span>
              )}

              <span className="ml-auto text-[10px] text-ink-500">
                Transport paused while you're in here
              </span>
              <button
                className={clsx('btn', previewing && 'btn-active')}
                onClick={() => void togglePreview()}
              >
                {previewing ? <Square size={13} /> : <Play size={13} />}
                {previewing ? 'Stop' : 'Preview loop'}
              </button>
              <button className="btn btn-primary" onClick={importSelection}>
                <Download size={13} />
                Import this loop
              </button>
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="label">
                  Suggestions — {bars}-bar windows ranked by repetition
                </span>
                {candidates.length > 1 && (
                  <button className="btn ml-auto" onClick={importAll}>
                    <Wand2 size={12} />
                    Import all {candidates.length}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-1.5">
                {candidates.map((c) => {
                  const active = c.bar === startBar + 1;
                  return (
                    <button
                      key={c.bar}
                      onClick={() => setStartBar(c.bar - 1)}
                      className={clsx(
                        'rounded-lg border px-2 py-1.5 text-left transition-colors',
                        active
                          ? 'border-volt-500 bg-volt-500/10'
                          : 'border-ink-700 bg-ink-850 hover:border-ink-500',
                      )}
                    >
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[11px] font-medium">bar {c.bar}</span>
                        <span className="mono text-[9px] text-ink-400">
                          {timecode(c.startSec)}
                        </span>
                        <span className="mono ml-auto text-[10px] text-amber-hot">
                          {Math.round(c.score * 100)}
                        </span>
                      </div>
                      <div className="mt-1 h-0.5 overflow-hidden rounded-full bg-ink-700">
                        <div
                          className="h-full bg-amber-hot"
                          style={{ width: `${Math.round(c.repetition * 100)}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
                {!candidates.length && (
                  <p className="text-[11px] text-ink-500">
                    No windows long enough at this loop length — try fewer bars.
                  </p>
                )}
              </div>
            </div>

            <p className="text-[10px] leading-relaxed text-ink-500">
              Space previews · drag the green region to move it · ← → move one snap step · shift +
              ← → move a full loop. If the grid drifts,
              correct the BPM first, then the offset — offset only shifts, BPM changes the spacing.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
